import { POLL_OPTION_MAX_LENGTH, visibilityTypeValues, } from '../types/index.js';
import { canModerateJam } from './jamHelpers.js';
import { sameRecurrenceId } from './eventRecurrence.js';
import { checkGroupCapabilities, checkPostCapabilities, checkRoleCapabilities, } from './capabilitiesHelpers.js';
import { scopeMatches } from './scopeHelpers.js';
import { countBy, dateSorter, groupBy, transformValues } from './utils.js';
const buildThread = (rootPost, postList) => ({
    ...rootPost,
    children: postList
        .filter((p) => p.inReplyToId === rootPost.id)
        .map((post) => buildThread(post, postList)),
});
export const buildThreads = (posts) => posts
    .filter((p) => !posts.find((p2) => p2.id === p.inReplyToId))
    .map((post) => buildThread(post, posts))
    .sort(dateSorter());
export const getReactionCount = (post) => {
    return countBy(post.reactions, (r) => r.reaction);
};
export const countVotes = (optionsCount, answers) => {
    const voteCounts = new Array(optionsCount).fill(0);
    for (const answer of answers) {
        for (const index of answer.selection) {
            if (index < optionsCount) {
                voteCounts[index] = voteCounts[index] + 1;
            }
        }
    }
    return voteCounts;
};
/** Fill blank poll choices with their placeholder label (e.g. "Option 1"). */
export const resolvePollOptionContents = (options, fallbackLabel) => options.map((text, index) => {
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : fallbackLabel(index);
});
/** Longer choices are rejected by `questionSchema`, so block submit instead. */
export const pollOptionsWithinLimit = (options) => options.every((text) => text.trim().length <= POLL_OPTION_MAX_LENGTH);
export const collectVotes = (post) => {
    if (post.type !== 'question') {
        return {
            voteCounts: [],
            votes: [],
        };
    }
    const postData = post.data;
    const sortedVoteEntries = post.entries
        .filter((e) => e.type === 'answer')
        .sort(dateSorter());
    const votesByProfile = groupBy(sortedVoteEntries, (e) => e.profile.id);
    const lastVoteByProfile = transformValues(votesByProfile, (votes) => votes.slice(-1)[0]);
    const votes = [...Object.values(lastVoteByProfile)]
        .filter((ve) => ve.data.selection.length > 0)
        .map((entry) => ({
        profile: entry.profile,
        selection: entry.data.selection,
    }));
    const voteCounts = countVotes(postData.options.length, votes);
    return {
        votes,
        voteCounts,
    };
};
const latestRsvpPerProfile = (rsvps, recurrenceId) => {
    const scoped = rsvps.filter((rsvp) => {
        if (recurrenceId) {
            if (rsvp.recurrenceId) {
                return sameRecurrenceId(rsvp.recurrenceId, recurrenceId);
            }
            return true;
        }
        return !rsvp.recurrenceId;
    });
    const rsvpsByProfile = groupBy(scoped, (r) => r.profile.id);
    return Object.values(rsvpsByProfile).map((profileRsvps) => {
        const instanceRsvps = recurrenceId
            ? profileRsvps.filter((rsvp) => sameRecurrenceId(rsvp.recurrenceId, recurrenceId))
            : [];
        const seriesRsvps = profileRsvps.filter((rsvp) => !rsvp.recurrenceId);
        const pool = recurrenceId && instanceRsvps.length > 0 ? instanceRsvps : seriesRsvps;
        const sorted = pool.sort(dateSorter());
        return sorted[sorted.length - 1];
    });
};
export const calculateEffectiveRsvps = (post, recurrenceId) => latestRsvpPerProfile(post.rsvps || [], recurrenceId).filter((rsvp) => !!rsvp);
export const getEffectiveRsvp = (post, profileId, recurrenceId) => calculateEffectiveRsvps(post, recurrenceId).find((r) => r.profile.id === profileId);
export const countYesRsvps = (post, recurrenceId) => calculateEffectiveRsvps(post, recurrenceId).filter((r) => r.response === 'yes').length;
export const isCapacityEvent = (event) => !!event.maxAttendees;
export const parseEventMaxAttendeesInput = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed)
        return undefined;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};
export const withoutEventMaxAttendees = (event) => {
    const { maxAttendees: _, ...rest } = event;
    return rest;
};
export const normalizeEventDataForSave = (event) => {
    const { maxAttendees } = event;
    if (maxAttendees == null ||
        !Number.isFinite(maxAttendees) ||
        maxAttendees < 1) {
        return withoutEventMaxAttendees(event);
    }
    return { ...event, maxAttendees: Math.trunc(maxAttendees) };
};
/** ArangoDB may store null when capacity was cleared; strip for validation/output. */
export const normalizeEventDataFromDb = (event) => {
    if (event.maxAttendees != null) {
        return event;
    }
    return withoutEventMaxAttendees(event);
};
export const normalizePostDataFromDb = (data) => data.type === 'event' ? normalizeEventDataFromDb(data) : data;
export const eventDataForDbUpdate = (previous, normalized) => {
    if ((previous === null || previous === void 0 ? void 0 : previous.maxAttendees) != null && !('maxAttendees' in normalized)) {
        return { ...normalized, maxAttendees: null };
    }
    return normalized;
};
export const canManageEventRsvps = (profile, post) => !!(profile &&
    (post.profile.id === profile.id || canModerateJam(profile, post)));
export const getJamCapacityJoinBlock = (post, profile, recurrenceId) => {
    var _a;
    const event = ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) === 'event' ? post.data : undefined;
    if (!(event === null || event === void 0 ? void 0 : event.maxAttendees) || !(profile === null || profile === void 0 ? void 0 : profile.id)) {
        return { blocked: false };
    }
    if (canModerateJam(profile, post)) {
        return { blocked: false };
    }
    const myRsvp = getEffectiveRsvp(post, profile.id, recurrenceId);
    if ((myRsvp === null || myRsvp === void 0 ? void 0 : myRsvp.response) === 'yes') {
        return { blocked: false };
    }
    if ((myRsvp === null || myRsvp === void 0 ? void 0 : myRsvp.response) === 'removed') {
        return { blocked: true, reason: 'removed' };
    }
    if (countYesRsvps(post, recurrenceId) >= event.maxAttendees) {
        return { blocked: true, reason: 'full' };
    }
    return { blocked: true, reason: 'rsvp-required' };
};
export const canDeletePost = (authData, post, config) => checkPostCapabilities(authData, ['core-posts-delete'], post, config).success;
export const canCreatePost = (authData, type, visibility, group) => {
    var _a;
    return visibility === 'group'
        ? !!group &&
            checkGroupCapabilities(authData, [`core-posts-create-${type}`], group)
                .success
        : checkRoleCapabilities((_a = authData.profile) === null || _a === void 0 ? void 0 : _a.roles, [
            `core-posts-create-${type}-${visibility}`,
        ]).success &&
            scopeMatches({
                scopes: authData.scopes,
                requiredScope: { scopeLevel: 'write', resource: { type: 'posts' } },
            });
};
export const canCreatePostTypeInAnyGroup = (authData, type) => {
    var _a, _b;
    return scopeMatches({
        scopes: authData.scopes,
        requiredScope: { scopeLevel: 'write', resource: { type: 'groups' } },
    }) &&
        !!((_b = (_a = authData.profile) === null || _a === void 0 ? void 0 : _a.memberships) === null || _b === void 0 ? void 0 : _b.some((m) => checkGroupCapabilities(authData, [`core-posts-create-${type}`], m.group).success));
};
export const canCreatePostTypeWithVisibility = (authData, type, visibility) => {
    var _a;
    return visibility === 'group'
        ? canCreatePostTypeInAnyGroup(authData, type)
        : checkRoleCapabilities((_a = authData.profile) === null || _a === void 0 ? void 0 : _a.roles, [
            `core-posts-create-${type}-${visibility}`,
        ]).success &&
            scopeMatches({
                scopes: authData.scopes,
                requiredScope: { scopeLevel: 'write', resource: { type: 'posts' } },
            });
};
export const canCreatePostType = (authData, type) => visibilityTypeValues.some((visibility) => canCreatePostTypeWithVisibility(authData, type, visibility));
/**
 * Whether Reply / Repost / Like should look live. Non-members of a group
 * post stay fully disabled (existing feed rule). Each action also follows
 * the capability the matching write endpoint actually checks: feed replies
 * and reposts need create-*, likes need core-posts-react.
 */
export const getPostActionAvailability = (authData, post, config) => {
    var _a, _b, _c;
    const isGroupPost = !!post.groupId || !!post.group;
    const membershipExists = !!((_b = (_a = authData.profile) === null || _a === void 0 ? void 0 : _a.memberships) === null || _b === void 0 ? void 0 : _b.some((m) => { var _a; return m.group.id === ((_a = post.group) === null || _a === void 0 ? void 0 : _a.id) || m.group.id === post.groupId; }));
    if (isGroupPost && !membershipExists) {
        return { canReply: false, canRepost: false, canReact: false };
    }
    const canReply = checkPostCapabilities(authData, ['core-posts-reply'], post, config)
        .success &&
        canCreatePost(authData, 'note', post.visibility, (_c = post.group) !== null && _c !== void 0 ? _c : undefined);
    const canReact = checkPostCapabilities(authData, ['core-posts-react'], post, config).success;
    const canRepost = post.group
        ? checkGroupCapabilities(authData, [`core-posts-create-${post.type}`], post.group).success
        : canCreatePost(authData, post.type, post.visibility);
    return { canReply, canRepost, canReact };
};
